import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Share } from 'lucide-react';

const PWAInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Check if dismissed via sessionStorage to prevent annoying the user on every navigation 
        // during the same browsing session, but asks again if they return later.
        const dismissed = sessionStorage.getItem('pwaPromptDismissed');
        if (dismissed) return;

        // Detect iOS devices
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isAppleDevice = /iphone|ipad|ipod/.test(userAgent);
        const isStandalone = window.navigator.standalone === true;

        if (isAppleDevice && !isStandalone) {
            setIsIOS(true);
            setShowPrompt(true); // Display IMMEDIATELY for iOS
        }

        // Detect Android/Desktop installation event
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault(); // Prevent the default mini-infobar from appearing
            setDeferredPrompt(e);
            setShowPrompt(true); // Display IMMEDIATELY for Android/Desktop
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setShowPrompt(false);
            }
            setDeferredPrompt(null);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        sessionStorage.setItem('pwaPromptDismissed', 'true');
    };

    return (
        <AnimatePresence>
            {showPrompt && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    dir="rtl"
                    className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-8 bg-slate-900/90 backdrop-blur-xl border-t border-slate-700 shadow-2xl rounded-t-3xl"
                >
                    <button 
                        onClick={handleDismiss}
                        className="absolute top-3 left-3 p-2 bg-slate-800 rounded-full text-slate-400 hover:text-white"
                    >
                        <X size={16} />
                    </button>

                    <div className="flex flex-col items-center text-center mt-2 px-4">
                        <div className="bg-teal-500/20 p-4 rounded-3xl mb-4">
                            <Download size={32} className="text-teal-400" />
                        </div>
                        
                        <h3 className="text-xl font-bold text-white mb-2">تثبيت التطبيق</h3>
                        
                        {isIOS ? (
                            <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                                للحصول على أفضل تجربة، قم بإضافة التطبيق إلى الشاشة الرئيسية. اضغط على أيقونة المشاركة <Share size={14} className="inline mx-1" /> ثم اختر <strong>"إضافة للشاشة الرئيسية" (Add to Home Screen)</strong>.
                            </p>
                        ) : (
                            <>
                                <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                                    قم بتثبيت تطبيق "بوابة القائد" على جهازك للوصول السريع لبياناتك وتجربة استخدام أفضل وأسرع!
                                </p>
                                <button
                                    onClick={handleInstall}
                                    className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-teal-500/30 transform transition hover:scale-105 active:scale-95"
                                >
                                    تثبيت الآن
                                </button>
                            </>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PWAInstallPrompt;